import { HeroMinimal } from "../components/sections";

function PrivacyPage() {
  const lastUpdated = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <HeroMinimal
        title="Privacy Policy"
        subtitle={`Last updated: ${lastUpdated}`}
        ctaText="Back to Home"
        ctaHref="/"
      />

      <section className="section" aria-labelledby="privacy-content">
        <div className="container">
          <div className="max-w-3xl mx-auto space-y-12">
            <article>
              <h2 id="introduction" className="h2 text-text-primary mb-4">
                Introduction
              </h2>
              <p className="body text-text-secondary mb-6">
                UniqReach ("we," "our," or "us") is committed to protecting your
                privacy. This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you visit our
                website, use our services, or interact with us.
              </p>
              <p className="body text-text-secondary">
                By using our website or services, you agree to the collection
                and use of information in accordance with this policy.
              </p>
            </article>

            <article>
              <h2
                id="information-we-collect"
                className="h2 text-text-primary mb-4"
              >
                Information We Collect
              </h2>
              <h3 className="h3 text-text-primary mb-3">
                Personal Information
              </h3>
              <p className="body text-text-secondary mb-4">
                We may collect personally identifiable information that you
                voluntarily provide to us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-secondary mb-6">
                <li>Name and contact information (email, phone)</li>
                <li>Company name and website</li>
                <li>Job title and role</li>
                <li>
                  Business information (industry, company size, target market)
                </li>
                <li>Sales process and outbound volume details</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="h3 text-text-primary mb-3">
                Automatically Collected Information
              </h3>
              <p className="body text-text-secondary mb-4">
                When you visit our website, we may automatically collect:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-secondary mb-6">
                <li>IP address and browser type</li>
                <li>Operating system and device information</li>
                <li>Pages visited and time spent</li>
                <li>Referring website</li>
                <li>Clickstream data</li>
              </ul>

              <h3 className="h3 text-text-primary mb-3">
                Cookies and Tracking Technologies
              </h3>
              <p className="body text-text-secondary mb-4">
                We use cookies, web beacons, and similar technologies to track
                activity on our website and hold certain information. You can
                instruct your browser to refuse all cookies or to indicate when
                a cookie is being sent.
              </p>
            </article>

            <article>
              <h2
                id="how-we-use-information"
                className="h2 text-text-primary mb-4"
              >
                How We Use Your Information
              </h2>
              <p className="body text-text-secondary mb-4">
                We use collected information for the following purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-secondary mb-6">
                <li>To provide, maintain, and improve our services</li>
                <li>To respond to your inquiries and communicate with you</li>
                <li>To schedule and conduct strategy calls</li>
                <li>To assess fit for our outbound systems</li>
                <li>
                  To send relevant updates and marketing communications (with
                  consent)
                </li>
                <li>To analyze website usage and improve user experience</li>
                <li>To comply with legal obligations</li>
              </ul>
            </article>

            <article>
              <h2
                id="sharing-information"
                className="h2 text-text-primary mb-4"
              >
                Sharing Your Information
              </h2>
              <p className="body text-text-secondary mb-4">
                We do not sell your personal information. We may share
                information in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-secondary mb-6">
                <li>
                  <strong>Service Providers:</strong> Third-party vendors who
                  perform services on our behalf (analytics, email, scheduling,
                  CRM)
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with a
                  merger, acquisition, or sale of assets
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law,
                  regulation, or legal process
                </li>
                <li>
                  <strong>With Consent:</strong> When you explicitly agree to
                  share information with third parties
                </li>
              </ul>
            </article>

            <article>
              <h2 id="data-retention" className="h2 text-text-primary mb-4">
                Data Retention
              </h2>
              <p className="body text-text-secondary mb-4">
                We retain your personal information only for as long as
                necessary to fulfill the purposes outlined in this policy,
                unless a longer retention period is required or permitted by
                law.
              </p>
            </article>

            <article>
              <h2 id="security" className="h2 text-text-primary mb-4">
                Data Security
              </h2>
              <p className="body text-text-secondary mb-4">
                We implement appropriate technical and organizational security
                measures to protect your personal information against
                unauthorized access, alteration, disclosure, or destruction.
                However, no method of transmission over the Internet or
                electronic storage is 100% secure.
              </p>
            </article>

            <article>
              <h2 id="your-rights" className="h2 text-text-primary mb-4">
                Your Rights
              </h2>
              <p className="body text-text-secondary mb-4">
                Depending on your jurisdiction, you may have the following
                rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-secondary mb-6">
                <li>Right to access your personal information</li>
                <li>Right to rectify inaccurate information</li>
                <li>Right to request deletion of your information</li>
                <li>Right to restrict or object to processing</li>
                <li>Right to data portability</li>
                <li>Right to withdraw consent</li>
                <li>Right to lodge a complaint with a supervisory authority</li>
              </ul>
              <p className="body text-text-secondary">
                To exercise these rights, contact us at hello@uniqreach.com.
              </p>
            </article>

            <article>
              <h2 id="third-party-links" className="h2 text-text-primary mb-4">
                Third-Party Links
              </h2>
              <p className="body text-text-secondary mb-4">
                Our website may contain links to third-party websites. We are
                not responsible for the privacy practices or content of those
                sites. We encourage you to review the privacy policies of any
                third-party sites you visit.
              </p>
            </article>

            <article>
              <h2 id="changes" className="h2 text-text-primary mb-4">
                Changes to This Policy
              </h2>
              <p className="body text-text-secondary mb-4">
                We may update this Privacy Policy from time to time. We will
                notify you of any changes by posting the new policy on this page
                with an updated "Last Updated" date.
              </p>
            </article>

            <article>
              <h2 id="contact" className="h2 text-text-primary mb-4">
                Contact Us
              </h2>
              <p className="body text-text-secondary mb-4">
                If you have questions about this Privacy Policy, please contact
                us:
              </p>
              <address className="not-italic text-text-secondary">
                <p>UniqReach</p>
                <p>Email: hello@uniqreach.com</p>
                <p>United States</p>
              </address>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}

export default PrivacyPage;
